import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDrag, useDrop } from 'react-dnd/dist';
import { useTheme, Divider } from '@mui/material';
import { Card, CardContent, Typography } from '@mui/material';
import { Paper } from '@mui/material';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Interface for DragAndDrop component props
interface DragAndDropProps {
  htmlContent: string;
  onVerify?: (result: boolean) => void;
  onSubmit: () => void;
  multiple?: boolean;
  onSaveProgress?: (data: { placedElements?: Record<string, string[]>; selectedOptions?: string[]; points: number }) => void;
  data?: {  placedElements?: Record<string, string[]>; selectedOptions?: string[]; points: number};
}

// Interface for DropZone
interface DropZone {
  id: string;
  label: string;
  multiple: boolean;
}

const shuffleArray = (array: { id: string; text: string }[]) => {
  return [...array]
    .map((item) => ({ item, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ item }) => item);
};

const DragAndDrop: React.FC<DragAndDropProps> = ({ htmlContent, onVerify, onSubmit, multiple, onSaveProgress, data }) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [placedElements, setPlacedElements] = useState<Record<string, string[]>>({});
  const [submitted, setSubmitted] = useState(false);
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [savedPlacedElements, setSavedPlacedElements] = useState<Record<string, string[]>>({});
  const [maxWidth, setMaxWidth] = useState<number>(0);
  const [feedback, setFeedback] = useState<Record<string, 'correct' | 'incorrect' | 'partial' | null>>({});
  const [isCompact, setIsCompact] = useState<boolean>(false);
  const { t } = useTranslation();
  
  useEffect(() => {
    const updateLayout = () => {
      if (containerRef.current) {
        const containerHeight = containerRef.current.offsetHeight;
        const containerWidth = containerRef.current.offsetWidth;
        const viewportHeight = window.innerHeight;
        
        // Kompakt-Modus für kleine Bildschirme oder Container
        setIsCompact(viewportHeight < 800 || containerWidth < 600);
        setContainerWidth(containerWidth);
      }
    };

    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  const parser = new DOMParser(); 
  const doc = parser.parseFromString(htmlContent, 'text/html'); 

  const shuffleArrayGeneric = <T,>(array: T[]): T[] => {
    return [...array].sort(() => Math.random() - 0.5);
  };

  const dragElements = useMemo(() => 
    shuffleArrayGeneric(
      Array.from(doc.querySelectorAll('.diadibi-drag-element')).map((el) => ({
        id: el.id,
        text: el.textContent?.trim() || '',
      }))
    ), [htmlContent] 
  );
  
  // Dynamische Größenberechnung ohne feste Skalierung
  useEffect(() => {
    const tempContainer = document.createElement("div");
    tempContainer.style.position = "absolute";
    tempContainer.style.visibility = "hidden";
    tempContainer.style.display = "flex";
    document.body.appendChild(tempContainer);
  
    let max = 0;
    dragElements.forEach(({ text }) => {
      const tempCard = document.createElement("div");  
      tempCard.style.fontWeight = "bold";  
      tempCard.style.fontSize = isCompact ? "0.75rem" : "0.85rem";
      tempCard.style.padding = "0.25rem"; 
      tempCard.style.border = "2px solid transparent"; 
      tempCard.style.width = "auto"; 
      tempCard.style.minWidth = isCompact ? "100px" : "120px";
      tempCard.textContent = text;
      
      tempContainer.appendChild(tempCard);
      max = Math.max(max, tempCard.offsetWidth); 
    });
  
    document.body.removeChild(tempContainer);
    setMaxWidth(Math.max(isCompact ? 100 : 120, max + 20)); 
  }, [dragElements, isCompact]);

  const dropZones: DropZone[] = Array.from(doc.querySelectorAll('.diadibi-drop-zone')).map((el) => {
    const normalizedId = el.id.trim().toLowerCase();
    return {
        id: normalizedId,
        label: el.previousElementSibling?.textContent?.trim() || '',
        multiple: !!Array.from(doc.querySelectorAll(`.diadibi-drag-element[correct-zone="${el.id}"]`)).length,
    };
  });

  useEffect(() => {
    if (data?.placedElements && Object.keys(data.placedElements).length > 0) {
      setSavedPlacedElements(data.placedElements);
      setPoints(data.points || 0);
      setSubmitted(true);
      setHasVerified(true);

      const newFeedback: Record<string, 'correct' | 'partial' | 'incorrect'> = {};
      const arraysEqual = (arr1: string[], arr2: string[]) => {
        return arr1.length === arr2.length && arr1.every((item) => arr2.includes(item));
      };

      if (data.points === dragElements.length) {
        dropZones.forEach((zone) => {
          newFeedback[zone.id] = 'correct';
        });
      } else if (data.points === 0) {
        dropZones.forEach((zone) => {
          newFeedback[zone.id] = 'incorrect';
        });
      } else {
        dropZones.forEach((zone) => {
          const correctItems = Array.from(
            doc.querySelectorAll(`.diadibi-drag-element[correct-zone="${zone.id}"]`)
          ).map((el) => el.id);

          if(data.placedElements){
            const itemsInZone = data.placedElements[zone.id] || []; 
            const correctCount = itemsInZone.filter((itemId) => correctItems.includes(itemId)).length;
            const incorrectCount = itemsInZone.length - correctCount;

            if (zone.multiple) {
              if (correctCount > 0) {
                newFeedback[zone.id] = incorrectCount === 0 
                  ? (correctCount === correctItems.length ? 'correct' : 'partial') 
                  : 'incorrect';
              }
            } else {
              if (itemsInZone.length === 0) {
                newFeedback[zone.id] = 'incorrect';
              } else {
                newFeedback[zone.id] = arraysEqual(itemsInZone, [zone.id]) ? 'correct' : 'incorrect';
              }
            }
          }
        });
      }
      setFeedback(newFeedback);
    } 
  }, [data, dragElements.length, dropZones]);

  const [zones, setZones] = useState<Record<string, string[]>>(
    Object.fromEntries(dropZones.map((zone) => [zone.id, []]))
  );

  const [dragZone, setDragZone] = useState<Record<string, boolean>>(
    Object.fromEntries(dragElements.map((el) => [el.id, true]))
  );

  const [points, setPoints] = useState<number>(0);
  const [hasVerified, setHasVerified] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);

  const moveElementToZone = (itemId: string, zoneId: string) => {
    if (isVerified || submitted) return;
  
    const newZones = { ...zones };
    const currentZone = newZones[zoneId];
    const zone = dropZones.find((zone) => zone.id === zoneId);
  
    if (zone?.multiple) {
      if (!currentZone.includes(itemId)) {
        newZones[zoneId] = [...currentZone, itemId];
      }
    } else {
      if (currentZone.length > 0) {
        const replacedItemId = currentZone[0];
        setDragZone((prev) => ({ ...prev, [replacedItemId]: true }));
      }
      newZones[zoneId] = [itemId];
    }
  
    Object.keys(newZones).forEach((otherZoneId) => {
      if (otherZoneId !== zoneId) {
        newZones[otherZoneId] = newZones[otherZoneId].filter((id) => id !== itemId);
      }
    });
  
    setZones(newZones);
    setDragZone((prev) => ({ ...prev, [itemId]: false }));
    setFeedback((prev) => ({ ...prev, [zoneId]: null }));
  };

  const handleVerify = () => {
    setHasVerified(true);
    setIsVerified(true);

    let score = 0; 
    const newFeedback: Record<string, 'correct' | 'incorrect' | 'partial'> = {};
  
    dropZones.forEach((zone) => {
      const correctItems = Array.from(
        doc.querySelectorAll(`.diadibi-drag-element[correct-zone="${zone.id}"]`)
      ).map((el) => el.id);
  
      const itemsInZone = zones[zone.id] || [];
  
      itemsInZone.forEach((itemId) => {
        if (correctItems.includes(itemId)) {
          newFeedback[itemId] = 'correct';
          score += 1;
        } else {
          newFeedback[itemId] = 'incorrect';
        }
      });
  
      if (zone.multiple) {
        const correctCount = itemsInZone.filter((itemId) => correctItems.includes(itemId)).length;
        const incorrectCount = itemsInZone.length - correctCount;
        newFeedback[zone.id] =
          correctCount > 0 ? (incorrectCount === 0 ? 'correct' : 'partial') : 'incorrect';
      } else {
        if(JSON.stringify(itemsInZone) === JSON.stringify([zone.id])) {
          setFeedback((prev) => ({ ...prev, [zone.id]: 'correct' }));
          newFeedback[itemsInZone[0]] = "correct";
          score += 1;
        }
        if (JSON.stringify(itemsInZone) !== JSON.stringify([zone.id])) {
          setFeedback((prev) => ({ ...prev, [zone.id]: 'incorrect' }));
          newFeedback[itemsInZone[0]] = "incorrect"; 
        }
      }
    });
  
    setFeedback(newFeedback);
    setPoints(Math.max(0, score));
  
    if (onSaveProgress) {
      onSaveProgress({
        placedElements: zones,
        points: Math.max(0, score),
      });
    }
  
    if (onVerify) {
      onVerify(score >= dropZones.length);
    }
    setIsSubmitted(true);
    onSubmit();
  };

  const DragElement = ({ id, text }: { id: string; text: string }) => {
    const [{ isDragging }, drag] = useDrag({
      type: "ELEMENT",
      item: { id },
      canDrag: !isVerified,
      collect: (monitor: any) => ({
        isDragging: monitor.isDragging(),
      }),
    });
  
    const borderColor = feedback[id] === 'correct'
      ? "#4CAF50"
      : feedback[id] === 'incorrect'
      ? "#F44336"
      : "#ddd";
  
    return (
      <Card
        ref={drag}
        sx={{
          minWidth: isCompact ? "100px" : "120px",
          maxWidth: isCompact ? "200px" : "250px",
          wordWrap: "break-word",
          whiteSpace: "normal",
          overflow: "hidden",
          textOverflow: "ellipsis",
          padding: isCompact ? "0.15rem" : "0.2rem",
          borderRadius: "4px",
          boxShadow: 1,
          border: `2px solid ${borderColor}`,
          opacity: isDragging ? 0.5 : 1,
          cursor: "grab",
          textAlign: "center",
          transition: "transform 0.2s ease",
          '&:hover': {
            transform: !isVerified ? 'scale(1.02)' : 'none',
          }
        }}
      >
        <CardContent sx={{ 
          padding: isCompact ? "0.2rem !important" : "0.3rem !important",
          '&:last-child': {
            paddingBottom: isCompact ? "0.2rem !important" : "0.3rem !important"
          }
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: isCompact ? "0.75rem" : "0.85rem",
              fontWeight: 500,
              lineHeight: 1.2,
              wordWrap: "break-word", 
              whiteSpace: "normal" 
            }}
          >
            {text}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const DropZone = ({ id, label }: DropZone) => {
    const [{ isOver }, drop] = useDrop({
      accept: "ELEMENT",
      drop: (item: { id: string }) => moveElementToZone(item.id, id),
      collect: (monitor: any) => ({
        isOver: monitor.isOver(),
      }),
    });

    const backgroundColor =
      feedback[id] === "correct"
        ? "#C8E6C9"
        : feedback[id] === 'partial'
        ? '#FFEB99'
        : feedback[id] === "incorrect"
        ? "#FFCDD2"
        : isOver
        ? "#e0f7fa"
        : "#fafafa";

    return (
      <Paper
        ref={drop}
        elevation={2}
        sx={{
          minWidth: isCompact ? "180px" : "220px",
          maxWidth: "100%",
          minHeight: isCompact ? "80px" : "100px",
          flex: `1 1 ${isCompact ? "180px" : "220px"}`,
          padding: isCompact ? "0.5rem" : "0.75rem",
          backgroundColor,
          border: "2px solid #ddd",
          borderRadius: "6px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          flexShrink: 0,
          gap: isCompact ? "0.25rem" : "0.5rem",
        }}
      >
        <Typography 
          lang="de"
          variant="subtitle1" 
          sx={{
            fontWeight: "bold",
            fontSize: isCompact ? "0.8rem" : "0.9rem",
            wordWrap: "break-word",
            whiteSpace: "normal",
            hyphens: "auto",
            textAlign: "center",
            lineHeight: 1.2,
            marginBottom: isCompact ? "0.25rem" : "0.5rem",
          }}
        >
          {label}
        </Typography>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "center",
            gap: isCompact ? "0.25rem" : "0.5rem",
          }}
        >
          {savedPlacedElements[id]?.map((itemId) => (
            <DragElement
              key={itemId}
              id={itemId}
              text={dragElements.find((el) => el.id === itemId)?.text || ""}
            />
          ))}
          {zones[id]?.map((itemId) => (
            <DragElement
              key={itemId}
              id={itemId}
              text={dragElements.find((el) => el.id === itemId)?.text || ""}
            />
          ))}
        </Box>
      </Paper>
    );
  };

  const titleText = doc.body.innerHTML.split('<drag-drop>')[0]?.trim();

  return (
    <div
      ref={containerRef}
      style={{
        padding: isCompact ? "0.75rem" : "1rem",
        maxWidth: '100%',
        width: '100%',
        height: 'auto',
        minHeight: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: isCompact ? "0.5rem" : "0.75rem",
        backgroundColor: theme.palette.background.default,
        borderRadius: '4px',
        boxSizing: 'border-box',
        overflow: 'visible',
      }}
    >
      {titleText && (
        <div
          style={{
            width: '100%',
            marginBottom: isCompact ? "0.5rem" : "0.75rem",
            fontSize: isCompact ? "0.9rem" : "1rem",
            fontWeight: 'bold',
            color: theme.palette.text.primary,
            textAlign: 'center',
            lineHeight: 1.3,
          }}
          dangerouslySetInnerHTML={{ __html: titleText }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: isCompact ? "0.5rem" : "0.75rem",
          justifyContent: "center",
          width: "100%",
          boxSizing: 'border-box',
        }}
      >
        {dropZones.map(({ id, label, multiple }) => (
          <DropZone key={id} id={id} label={label} multiple={multiple} />
        ))}
      </Box>

      <Divider sx={{ 
        width: '100%',
        backgroundColor: theme.palette.divider,
        margin: isCompact ? "0.25rem 0" : "0.5rem 0"
      }} />

      {Object.keys(savedPlacedElements).length === 0 && (
        <div
          style={{
            padding: isCompact ? "0.5rem" : "0.75rem",
            backgroundColor: "rgba(161, 183, 165, 0.1)",
            borderRadius: "4px",
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            gap: isCompact ? "0.25rem" : "0.5rem",
            justifyContent: "center",
            boxSizing: 'border-box',
          }}
        >
          {dragElements
            .filter(({ id }) => dragZone[id])
            .map(({ id, text }) => (
              <DragElement key={id} id={id} text={text} />
            ))}
        </div>
      )}

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        marginTop: isCompact ? "0.5rem" : "0.75rem",
      }}>
        {hasVerified || submitted ? (
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 500,
              fontSize: isCompact ? "0.85rem" : "0.9rem",
              textAlign: 'center',
              padding: isCompact ? "0.5rem" : "0.75rem",
            }}
          >
            Erreichte Punkte: {points} von {dragElements.length}
          </Typography>
        ) : (
          <button
            onClick={handleVerify}
            style={{
              padding: isCompact ? "0.6rem 1.2rem" : "0.75rem 1.5rem",
              backgroundColor: 'rgb(161, 183, 165)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: isCompact ? "0.8rem" : "0.85rem",
              boxShadow: theme.shadows[2],
              transition: 'all 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'rgb(141, 163, 145)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'rgb(161, 183, 165)';
            }}
          >
            {t("Überprüfen")}
          </button>
        )}
      </div>
    </div>
  );
};

export default DragAndDrop;